// Form cues for main lifts and common accessories.
//
// Looked up by exercise name via getCuesForExercise(name) — program exercise
// names are free text ('Triceps Extensions', 'Squat', 'DB Rows'), so entries
// match on lowercase aliases. Compound names like 'Curls/Pullups/Pulldowns'
// are ambiguous and deliberately return no cues.

const CUE_ENTRIES = [
  // === MAIN LIFTS ===
  {
    aliases: ['squat', 'low bar squat', 'high bar squat'],
    exclude: ['front', 'goblet', 'hack', 'sissy', 'split', 'wide stance'],
    cues: [
      'Big breath, brace hard before every rep',
      'Break at hips and knees together',
      'Knees track over toes — push them out',
      'Hit depth: hip crease below the knee',
      'Drive up through the whole foot, chest proud',
    ],
  },
  {
    aliases: ['bench press', 'bench'],
    exclude: ['close grip', 'incline', 'decline', 'db', 'dumbbell', 'machine', 'pin', 'floor'],
    cues: [
      'Shoulder blades pinched back and down onto the bench',
      'Feet planted, glutes on the bench, moderate arch',
      'Bar touches around the sternum, forearms vertical',
      'Press back toward the rack, not straight up',
    ],
  },
  {
    aliases: ['deadlift'],
    exclude: ['romanian', 'rdl', 'stiff', 'sumo only note'],
    cues: [
      'Bar over mid-foot, shins touch the bar',
      'Pull the slack out before breaking the floor',
      'Chest up, lats tight — protect the lower back',
      'Push the floor away; hips and shoulders rise together',
      'Finish with glutes, don\'t lean back',
    ],
  },
  {
    aliases: ['overhead press', 'ohp', 'press', 'military press'],
    exclude: ['db', 'dumbbell', 'machine', 'arnold', 'shoulder press', 'leg press', 'bench'],
    cues: [
      'Squeeze glutes and brace — no back lean',
      'Bar starts on the front delts, elbows slightly forward',
      'Press slightly back once past the forehead',
      'Finish with the bar over the back of the neck, shrug up',
    ],
  },
  {
    aliases: ['front squat'],
    cues: [
      'Elbows high, upper arms parallel to floor',
      'Bar rests on the shoulders, not the wrists',
      'Stay upright — let the elbows drop and the bar dumps',
    ],
  },
  {
    aliases: ['close grip bench'],
    cues: [
      'Hands just inside shoulder width — not touching',
      'Tuck elbows closer than regular bench',
      'Touch lower on the chest, drive with triceps',
    ],
  },
  {
    aliases: ['romanian deadlift', 'rdl', 'stiff-leg deadlift', 'stiff leg deadlift'],
    cues: [
      'Soft knees — hinge at the hips, push them back',
      'Bar stays glued to the thighs',
      'Stop when hamstrings hit their stretch, don\'t chase the floor',
      'Flat back throughout',
    ],
  },
  {
    aliases: ['rack pull'],
    cues: [
      'Same setup as a deadlift — lats tight, bar close',
      'Don\'t bounce the bar off the pins',
    ],
  },
  {
    aliases: ['paused squat', 'wide stance paused squat'],
    cues: [
      'Stay tight in the hole — don\'t relax at the bottom',
      'Full 2-3 count pause, no bounce out',
    ],
  },
  {
    aliases: ['power clean'],
    cues: [
      'Start like a deadlift, accelerate past the knees',
      'Full hip extension before the pull under',
      'Catch on the shoulders with elbows high',
    ],
  },

  // === CHEST / PRESSING ACCESSORIES ===
  {
    aliases: ['db incline press', 'incline bench press', 'incline press'],
    cues: [
      '30-45° incline — steeper turns it into shoulders',
      'Lower to upper chest, elbows ~45° from the body',
    ],
  },
  {
    aliases: ['db flat press', 'db press', 'dumbbell press', 'decline db press'],
    cues: [
      'Kick the DBs up with your knees to set up',
      'Deeper stretch than barbell — control the bottom',
      'Don\'t clang the DBs at the top',
    ],
  },
  {
    aliases: ['dips', 'weighted dips'],
    cues: [
      'Lean forward for chest, stay upright for triceps',
      'Shoulders down away from the ears',
      'Depth: upper arms to parallel — stop if the shoulder complains',
    ],
  },
  {
    aliases: ['cable flyes', 'cable fly', 'pec dec', 'db fly', 'dumbbell fly', 'flyes'],
    cues: [
      'Slight elbow bend, locked in place — it\'s a hug, not a press',
      'Feel the stretch, squeeze the chest to bring arms together',
    ],
  },
  {
    aliases: ['push-ups', 'pushups', 'bodyweight pushups'],
    cues: [
      'Body in one rigid line — squeeze glutes and abs',
      'Hands under shoulders, elbows ~45°',
      'Chest to floor every rep',
    ],
  },
  {
    aliases: ['machine chest press'],
    cues: [
      'Set the seat so handles line up with mid-chest',
      'Keep shoulder blades pinned back',
    ],
  },

  // === BACK ===
  {
    aliases: ['barbell rows', 'barbell row', 'pendlay row'],
    cues: [
      'Hinge to ~45° or lower, flat back',
      'Pull to the lower chest / upper stomach',
      'Squeeze shoulder blades, no jerking with the hips',
    ],
  },
  {
    aliases: ['db rows', 'db row', 'dumbbell row'],
    cues: [
      'Brace on the bench, back flat',
      'Pull the elbow to the hip, not the armpit',
      'No twisting to grind out reps',
    ],
  },
  {
    aliases: ['cable rows', 'seated row', 'chest-supported rows', 't-bar rows', 'seal rows', 'machine row'],
    cues: [
      'Chest tall — don\'t round forward to reach',
      'Drive elbows back, squeeze the blades together',
      'Control the return, keep tension',
    ],
  },
  {
    aliases: ['lat pulldowns', 'lat pulldown', 'pulldowns'],
    cues: [
      'Slight lean back, chest up to the bar',
      'Pull elbows down and into the sides',
      'Bar to the collarbone — never behind the neck',
    ],
  },
  {
    aliases: ['pull-ups', 'pullups', 'pull ups'],
    cues: [
      'Start from a dead hang, shoulders engaged',
      'Lead with the chest, chin over the bar',
      'Full lockout at the bottom of every rep',
    ],
  },
  {
    aliases: ['chin-ups', 'chinups', 'chins'],
    cues: [
      'Underhand grip, shoulder width',
      'Pull the elbows down to the ribs',
      'Control the negative — don\'t drop',
    ],
  },
  {
    aliases: ['face pulls'],
    cues: [
      'Rope at face height, pull towards the eyes',
      'Elbows high and wide, thumbs point behind you',
      'Light weight — this is for rear delts, not ego',
    ],
  },
  {
    aliases: ['straight arm pulldowns'],
    cues: [
      'Arms nearly straight, hinge slightly forward',
      'Sweep the bar to the thighs with the lats',
    ],
  },
  {
    aliases: ['back extensions', 'hyperextensions', 'back extension'],
    cues: [
      'Hinge at the hips, not the spine',
      'Squeeze glutes at the top, don\'t hyperextend',
    ],
  },
  {
    aliases: ['good mornings'],
    cues: [
      'Bar in the low-bar squat position, knees soft',
      'Push hips back until hamstrings load up',
      'Stay well shy of failure — spine loading is high',
    ],
  },
  {
    aliases: ['reverse hypers'],
    cues: [
      'Swing from the glutes and hams, not momentum',
      'Pause briefly at the top',
    ],
  },

  // === SHOULDERS ===
  {
    aliases: ['db shoulder press', 'machine shoulder press', 'arnold press', 'seated press'],
    cues: [
      'Don\'t flare the ribs — keep the trunk stacked',
      'Lower until DBs reach ear height',
    ],
  },
  {
    aliases: ['lateral raises', 'cable lateral raises', 'machine lateral raises', 'side raises'],
    cues: [
      'Lead with the elbows, slight bend held constant',
      'Raise to shoulder height, pinky slightly up',
      'Light weight, strict — no swinging',
    ],
  },
  {
    aliases: ['front raises'],
    cues: [
      'Raise to eye level, thumbs up or palms down',
      'Brace so the lower back doesn\'t arch',
    ],
  },
  {
    aliases: ['rear delt flyes', 'reverse pec dec', 'cable rear delt flyes', 'rear delt'],
    cues: [
      'Hinge over or use the pad — no body english',
      'Lead with the pinkies, arms wide',
      'Squeeze between the shoulder blades',
    ],
  },
  {
    aliases: ['band pull-aparts', 'band pull aparts'],
    cues: [
      'Arms straight, pull the band to the chest',
      'Squeeze the blades, control the return',
    ],
  },
  {
    aliases: ['upright rows'],
    cues: [
      'Wide-ish grip, pull to chest height only',
      'Stop lower if the shoulders pinch',
    ],
  },

  // === ARMS ===
  {
    aliases: ['triceps pushdowns', 'tricep pushdowns', 'pushdowns', 'cable pushdown'],
    cues: [
      'Keep elbows pinned by your sides',
      'Full lockout, squeeze the triceps',
      'Only the forearms move — no shoulder rocking',
    ],
  },
  {
    aliases: ['triceps extensions', 'tricep extensions', 'overhead triceps extensions', 'cable overhead extensions', 'db triceps extensions'],
    cues: [
      'Elbows stay pointed forward/up and tucked',
      'Deep stretch behind the head, full lockout',
      'Keep the upper arms still',
    ],
  },
  {
    aliases: ['skull crushers'],
    cues: [
      'Lower to the forehead or just behind the head',
      'Elbows tucked, upper arms vertical and still',
    ],
  },
  {
    aliases: ['triceps kickbacks', 'kickbacks'],
    cues: [
      'Upper arm parallel to the floor and frozen',
      'Extend to full lockout, pause a beat',
    ],
  },
  {
    aliases: ['barbell curls', 'curls', 'curl', 'db curls', 'db curl', 'cable curls', 'dumbbell curls'],
    exclude: ['hammer', 'preacher', 'incline', 'concentration', 'reverse', 'wrist', 'leg', 'nordic'],
    cues: [
      'Elbows pinned to the sides — don\'t let them drift forward',
      'No swinging from the hips',
      'Full range: dead straight at the bottom, full squeeze up top',
    ],
  },
  {
    aliases: ['hammer curls', 'hammer curl'],
    cues: [
      'Neutral grip (palms facing in) the whole way',
      'Elbows pinned, no swing',
    ],
  },
  {
    aliases: ['preacher curls'],
    cues: [
      'Armpits snug on the pad',
      'Control the bottom — don\'t bounce at full stretch',
    ],
  },
  {
    aliases: ['incline db curls'],
    cues: [
      'Let the arms hang back for the long-head stretch',
      'Curl without the shoulders creeping forward',
    ],
  },
  {
    aliases: ['concentration curls'],
    cues: [
      'Elbow braced on the inner thigh, fully still',
      'Slow squeeze, rotate pinky up at the top',
    ],
  },
  {
    aliases: ['reverse curls', 'wrist curls'],
    cues: [
      'Lighter than you think — forearms fatigue fast',
      'Strict elbows, controlled negative',
    ],
  },
  {
    aliases: ["farmer's walks", 'farmers walks', 'suitcase carries'],
    cues: [
      'Stand tall, shoulders packed, brace the core',
      'Short quick steps, don\'t let the weight tip you',
    ],
  },

  // === LEGS ===
  {
    aliases: ['leg press'],
    cues: [
      'Feet mid-platform, shoulder width',
      'Lower until hips start to tuck — no butt wink',
      'Never lock the knees hard at the top',
    ],
  },
  {
    aliases: ['bulgarian split squats', 'split squats'],
    cues: [
      'Rear foot laces-down on the bench',
      'Drop straight down, front shin near vertical',
      'Lean slightly forward for glutes, upright for quads',
    ],
  },
  {
    aliases: ['lunges'],
    cues: [
      'Long enough step that the front knee stays behind the toes',
      'Push through the front heel to stand',
    ],
  },
  {
    aliases: ['leg extensions'],
    cues: [
      'Pad on the shins just above the ankle',
      'Pause at full lockout, control down',
    ],
  },
  {
    aliases: ['leg curls', 'seated leg curls', 'lying leg curls'],
    cues: [
      'Hips stay pinned to the pad',
      'Full squeeze at the top, slow negative',
    ],
  },
  {
    aliases: ['nordic curls'],
    cues: [
      'Hips extended — body straight from knees to head',
      'Fight the descent as long as possible',
    ],
  },
  {
    aliases: ['hack squats', 'goblet squats', 'sissy squats'],
    cues: [
      'Knees track over toes',
      'Full depth, control the bottom',
    ],
  },
  {
    aliases: ['hip thrusts', 'hip thrust', 'glute bridges', 'glute bridge'],
    cues: [
      'Shoulder blades on the bench, chin tucked',
      'Drive through the heels to full hip lockout',
      'Squeeze glutes hard at the top — no back arch',
    ],
  },
  {
    aliases: ['cable pull-throughs'],
    cues: [
      'Face away, hinge with a flat back',
      'Snap the hips forward, squeeze glutes',
    ],
  },
  {
    aliases: ['cable kickbacks'],
    cues: [
      'Slight forward lean, kick back and up with the glute',
      'Don\'t arch the lower back to fake range',
    ],
  },
  {
    aliases: ['standing calf raises', 'seated calf raises', 'leg press calf raises', 'calf raises'],
    cues: [
      'Full stretch at the bottom — heel below the toes',
      'Pause one second at the top, no bouncing',
    ],
  },

  // === CORE ===
  {
    aliases: ['planks', 'plank'],
    cues: [
      'Straight line from head to heels',
      'Squeeze glutes and brace abs — no sagging hips',
    ],
  },
  {
    aliases: ['side planks'],
    cues: [
      'Elbow under the shoulder, hips stacked',
      'Push the hips up — don\'t let them droop',
    ],
  },
  {
    aliases: ['ab wheel rollouts', 'ab wheel'],
    cues: [
      'Tuck the pelvis, brace before rolling out',
      'Only go as far as you can without the back arching',
    ],
  },
  {
    aliases: ['cable crunches'],
    cues: [
      'Kneel, rope beside the head',
      'Crunch the ribs to the pelvis — hips stay still',
    ],
  },
  {
    aliases: ['hanging leg raises', 'leg raises'],
    cues: [
      'Curl the pelvis up, don\'t just swing the legs',
      'Slow negative, no momentum',
    ],
  },
  {
    aliases: ['decline sit-ups'],
    cues: [
      'Curl up one vertebra at a time',
      'Don\'t yank the neck with your hands',
    ],
  },
  {
    aliases: ['dead bugs'],
    cues: [
      'Lower back pressed flat to the floor throughout',
      'Opposite arm and leg, slow and controlled',
    ],
  },
  {
    aliases: ['pallof press'],
    cues: [
      'Press straight out, resist the twist',
      'Ribs down, glutes tight',
    ],
  },
  {
    aliases: ['russian twists', 'woodchoppers'],
    cues: [
      'Rotate from the trunk, not the arms',
      'Keep the movement smooth — no flinging',
    ],
  },
];

// Normalize a program/library exercise name for matching
function normalize(name) {
  return String(name || '')
    .toLowerCase()
    .replace(/\((bfr|emom|no bfr|ramp[^)]*|paused|beltless|wide stance)[^)]*\)/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

// Return an array of cue strings for an exercise name, or null.
// Compound names ('Curls/Pullups/Pulldowns') are ambiguous → null.
export function getCuesForExercise(name) {
  const n = normalize(name);
  if (!n || n.includes('/')) return null;

  // Tiered scoring: an exact alias match always wins; a name that contains an
  // alias ('weighted dips' ⊃ 'dips') beats an alias that merely contains the
  // name ('incline db curls' ⊃ 'db curls'), so 'Bench Press' can never be
  // captured by the 'incline bench press' alias. Within a tier the longest
  // (most specific) alias wins, so 'front squat' beats 'squat'.
  let best = null;
  let bestScore = 0;
  for (const entry of CUE_ENTRIES) {
    if (entry.exclude && entry.exclude.some(x => n.includes(x))) continue;
    for (const alias of entry.aliases) {
      let score = 0;
      if (n === alias) score = 1000;
      else if (n.includes(alias)) score = 500 + alias.length;
      else if (n.length >= 4 && alias.includes(n)) score = 100 - (alias.length - n.length);
      if (score > bestScore) { best = entry; bestScore = score; }
    }
  }
  return best ? best.cues : null;
}

export default CUE_ENTRIES;
